import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import {
  CapitalizePipe,
  DeleteDialogComponent,
  DialogResult,
  DocumentLinksComponent,
  DocumentTagsService,
  errorToast,
  FileUploadComponent,
  FormErrorService,
  getKendoWeekNumber,
  Media,
  MediaService,
  RcMultiSelectWrapPaddingDirective,
  RcPanelBarNoCollapseDirective,
  replaceUndefinedByNull,
  successSaveToast,
  SupplierInvoice,
  SupplierInvoiceFee,
  SupplierInvoiceFeeService,
  SupplierInvoiceService,
  SupplierInvoiceStatus,
  UserNotificationService,
  ValidationDirective,
  DropdownLoaderDirective,
} from 'shared';
import {
  PanelBarComponent,
  PanelBarContentDirective,
  PanelBarExpandMode,
  PanelBarItemComponent,
} from '@progress/kendo-angular-layout';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { debounceTime, Observable, of, Subject, take } from 'rxjs';
import { FormFieldComponent, SwitchComponent } from '@progress/kendo-angular-inputs';
import { LabelComponent } from '@progress/kendo-angular-label';
import { DatePipe, NumberFormatOptions } from '@progress/kendo-angular-intl';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { SupplierInvoiceFeePopupComponent } from '@features/purchase/supplier-invoices/supplier-invoice-fee-popup/supplier-invoice-fee-popup.component';
import { distinctUntilChanged } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { environment } from '@environments';
import { DocumentFeeCardComponent } from '@features/fee/document-fee-card/document-fee-card.component';

@Component({
  selector: 'rc-supplier-invoice-sidebar-panel',
  imports: [
    DocumentFeeCardComponent,
    ButtonComponent,
    CapitalizePipe,
    PanelBarComponent,
    PanelBarItemComponent,
    PanelBarContentDirective,
    FormFieldComponent,
    LabelComponent,
    TranslateModule,
    DatePipe,
    ReactiveFormsModule,
    UpperCasePipe,
    LoaderComponent,
    SwitchComponent,
    FileUploadComponent,
    AsyncPipe,
    MultiSelectComponent,
    RcMultiSelectWrapPaddingDirective,
    RcPanelBarNoCollapseDirective,
    ValidationDirective,
    DocumentLinksComponent,
    DropdownLoaderDirective,
  ],
  providers: [TranslateModule, CapitalizePipe, DialogRef, FormErrorService, UserNotificationService],
  templateUrl: './supplier-invoice-sidebar-panel.component.html',
  styleUrl: './supplier-invoice-sidebar-panel.component.scss',
  standalone: true,
})
export class SupplierInvoiceSidebarPanelComponent {
  protected readonly getKendoWeekNumber = getKendoWeekNumber;
  protected readonly environment = environment;

  #dialogService = inject(DialogService);
  #supplierInvoiceFeeService = inject(SupplierInvoiceFeeService);
  #notificationService = inject(NotificationService);
  #formErrorService = inject(FormErrorService);
  #mediaService = inject(MediaService);
  #capitalizePipe = inject(CapitalizePipe);
  #translateService = inject(TranslateService);
  extraCosts$: Observable<SupplierInvoiceFee[]> = of([]);
  #supplierInvoiceService = inject(SupplierInvoiceService);
  #documentTagService = inject(DocumentTagsService);
  tags$ = this.#documentTagService.getDocumentTags();
  #tagSubject$ = new Subject<string>();
  uploadUrl = 'api/supplier_invoice_medias';
  #dialog = inject(DialogRef);
  decimalFormat: NumberFormatOptions = {
    style: 'decimal',
    minimumFractionDigits: 2,
  };
  #router = inject(Router);
  supplierInvoice = input<SupplierInvoice>();
  // supplierInvoiceSection = input<SupplierInvoiceSection>();
  isSupplierInvoiceLocked = computed<boolean>(() => this.supplierInvoice()!.status !== SupplierInvoiceStatus.NEW);
  //TODO: @Pieter check what if complaints (used to be a status)
  isDoNotInvoiceSwitchLocked = computed<boolean>(() =>
    [SupplierInvoiceStatus.PROCESSED].includes(this.supplierInvoice()!.status as SupplierInvoiceStatus),
  );
  readonly = input<boolean>(false);
  loading = signal<boolean>(false);
  public expandMode: PanelBarExpandMode = 1;
  updateTotals = output<boolean>();
  supplierInvoiceForm: FormGroup<SupplierInvoiceAttachmentsForm> = this.initSupplierInvoiceAttachmentsForm();
  files$: Observable<Media[]> = of<Media[]>([]);

  fileUrl(file: Media): string {
    return `${environment.apiUrl}${file.contentUrl}?disposition=inline`;
  }

  openFileInWindow(event: MouseEvent, file: Media) {
    // Only hijack a plain left click: ctrl/cmd/middle click keeps the native
    // "open in new tab" behaviour of the anchor.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();

    const width = Math.min(1000, window.screen.availWidth);
    const height = Math.min(900, window.screen.availHeight);
    const left = (window.screen.availWidth - width) / 2;
    const top = (window.screen.availHeight - height) / 2;
    const features = `popup=yes,noopener,width=${width},height=${height},left=${left},top=${top}`;

    // Blocked popup: fall back to the anchor's normal navigation.
    if (!window.open(this.fileUrl(file), '_blank', features)) {
      window.open(this.fileUrl(file), '_blank', 'noopener');
    }
  }
  #updateSubject = new Subject<boolean>();


  constructor() {
    effect(() => {
      const si = this.supplierInvoice();
      if (si) {
        this.files$ = this.#mediaService.getAllMedias(`${si['@id']}/medias`);
        this.extraCosts$ = this.#supplierInvoiceFeeService.getSupplierInvoiceFeesForSupplierInvoice(
          this.supplierInvoice()!['@id']!,
        );
        this.patchSupplierInvoiceForm(si);
        this.getFiles();
      }
    });
    this.#updateSubject.pipe(debounceTime(600)).subscribe(() => {
      this.#updateSupplierInvoice();
    });

    this.#tagSubject$.pipe(debounceTime(300)).subscribe((searchTerm) => {
      this.tags$ = this.#documentTagService.findDocumentTagsByName(searchTerm);
    });
  }

  createExtraCost() {
    const title = 'CREATE_DIALOG_TITLE';
    this.#dialog = this.#dialogService.open({
      content: SupplierInvoiceFeePopupComponent,
      title: this.#capitalizePipe.transform(
        this.#translateService.instant(title, {
          value: this.#translateService.instant('EXTRA_COST'),
        }),
      ),
    });
    this.#dialog.content.instance.supplierInvoice.set(this.supplierInvoice()!['@id']);
    this.#dialog.result.subscribe(() => {
      // this.extraCosts$ = this.#supplierInvoiceFeeService.getSupplierInvoiceFeesForSupplierInvoice(
      //   this.supplierInvoice()!['@id']!,
      // );
      this.updateTotals.emit(true);
    });
  }

  private patchSupplierInvoiceForm(supplierInvoice: SupplierInvoice) {
    this.supplierInvoiceForm.patchValue({
      media: supplierInvoice.media as unknown as Media[],
      tags: supplierInvoice.tags ?? [],
    });
  }

  deleteExtraCost(fee: any) {
    const iri = fee['@id'];
    if (iri) {
      this.#supplierInvoiceFeeService.delete(iri).subscribe(() => {
        this.updateTotals.emit(true);
      });
    }
  }

  getFiles() {
    this.loading.set(true);
    this.files$.subscribe((media) => {
      this.supplierInvoiceForm.patchValue({
        media: media,
      });
      this.loading.set(false);
    });
  }

  openDeleteDialog(fee: any) {
    const title = 'DELETE_DIALOG_TITLE';
    this.#dialog = this.#dialogService.open({
      content: DeleteDialogComponent,
      title: this.#capitalizePipe.transform(
        this.#translateService.instant(title, { value: this.#translateService.instant('EXTRA_COST') }),
      ),
    });

    this.#dialog.content.instance.value = `${fee.description}: ${fee.amount}`;
    this.#dialog.result.subscribe((value: DialogResult) => {
      if (value.delete) {
        this.deleteExtraCost(fee);
      }
    });
  }

  markAsDirty(update: { status: boolean; attachments: Media[] }) {
    const media = update.attachments.map((att) => ({ ...att, supplierInvoice: this.supplierInvoice()!['@id']! }));

    this.supplierInvoiceForm.patchValue({
      media: media,
    });
    this.onSubmit();
  }

  private initSupplierInvoiceAttachmentsForm() {
    const agreementForm = new FormGroup<SupplierInvoiceAttachmentsForm>({
      media: new FormControl([], {
        nonNullable: true,
        validators: [],
      }),
      tags: new FormControl([], {
        nonNullable: true,
        validators: [],
      }),
    });
    agreementForm.updateValueAndValidity();
    return agreementForm;
  }

  updateLinkedSalesInvoice() {
    console.log('update linked sales invoice');
  }

  updateTags(event: string[]) {
    this.supplierInvoiceForm.patchValue({ tags: event });
    this.#updateSubject.next(true);
  }

  updateDoNotInvoice(value: boolean) {
    if (this.supplierInvoice()?.doNotInvoice === value) {
      return;
    }
    const si = { doNotInvoice: value } as SupplierInvoice;
    this.#supplierInvoiceService.patch(this.supplierInvoice()!['@id']!, si).subscribe({
      next: (response: SupplierInvoice) => {
        const notification = this.#notificationService.show(successSaveToast);
        if (notification.content) {
          const si = this.#capitalizePipe.transform(this.#translateService.instant('SUPPLIER_INVOICE'));
          notification.content.instance.value = `${si}: ${response.documentNumber}`;
          this.updateTotals.emit(true);
        }
      },
      error: (err: HttpErrorResponse) => {
        const notification = this.#notificationService.show(errorToast);
        if (notification.content) {
          notification.content.instance.setCurrentValue(err);
        }
        this.#formErrorService.setErrorViolations(err);
      },
    });
  }

  onSubmit() {
    let action;
    replaceUndefinedByNull(this.supplierInvoiceForm.value);
    const files = this.supplierInvoiceForm.value.media ?? [];
    this.supplierInvoiceForm.patchValue({
      media: this.#mediaIris(files) as unknown as Media[],
    });
    action = this.#supplierInvoiceService.patch(
      this.supplierInvoice()!['@id']!,
      this.supplierInvoiceForm.value as unknown as SupplierInvoice,
    );
    action.pipe(take(1), distinctUntilChanged()).subscribe({
      next: (response: SupplierInvoice) => {
        this.files$ = this.#mediaService.getAllMedias(`${response['@id']}/medias`);
        this.getFiles();
        const notification = this.#notificationService.show(successSaveToast);
        if (notification.content) {
          const si = this.#capitalizePipe.transform(this.#translateService.instant('SUPPLIER_INVOICE'));
          notification.content.instance.value = `${si}: ${this.supplierInvoice()?.documentNumber}`;
        }
      },
      error: (err: HttpErrorResponse) => {
        const notification = this.#notificationService.show(errorToast);
        if (notification.content) {
          notification.content.instance.setCurrentValue(err);
        }
        this.#formErrorService.setErrorViolations(err);
      },
    });
  }

  #updateSupplierInvoice() {
    this.supplierInvoiceForm.patchValue({
      media: this.#mediaIris(this.supplierInvoiceForm.controls.media.value) as unknown as Media[],
    });
    this.#supplierInvoiceService
      .patch(this.supplierInvoice()!['@id']!, this.supplierInvoiceForm.value as unknown as SupplierInvoice)
      .subscribe({
        next: (value) => {
          this.patchSupplierInvoiceForm(value);
          this.updateTotals.emit(true);
          const notification = this.#notificationService.show(successSaveToast);
          if (notification.content) notification.content.instance.value = 'SUPPLIER_INVOICE';
        },
        error: (err: HttpErrorResponse) => {
          const notification = this.#notificationService.show(errorToast);
          if (notification.content) {
            notification.content.instance.setCurrentValue(err);
          }

          this.#formErrorService.setErrorViolations(err);
        },
      });
  }

  /** The form holds the attachments as objects (loaded or uploaded) or as IRIs (from the invoice): send IRIs. */
  #mediaIris(media: (Media | string)[] | null | undefined): string[] {
    return (media ?? []).map((m) => (typeof m === 'string' ? m : m['@id']!));
  }

  filterTags(searchTerm: string) {
    this.#tagSubject$.next(searchTerm);
  }
}


interface SupplierInvoiceAttachmentsForm {
  media: FormControl<Media[]>;
  tags: FormControl<string[]>;
}
